/**
 * Query Builder Service
 * Provides a unified service for building API requests with the new filter system
 */

import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CommonFilters,
  FilterField,
  FilterGroup,
  PaginationConfig,
  QueryConfig,
  ScopeConfig,
  SortConfig,
  SortDirection
} from '../models/filter.models';
import { FilterBuilderUtil } from '../utils/filter-builder.util';
import { BaseApiService } from './base-api.service';
import { CustomKeycloakService } from './keycloak.service';

@Injectable({
  providedIn: 'root'
})
export class QueryBuilderService extends BaseApiService {
  override baseUrl: string = environment.baseUrl;

  constructor(
    http: HttpClient,
    @Inject(Router) router: Router,
    @Optional() keycloakService?: CustomKeycloakService
  ) {
    super(http, router, keycloakService);
  }

  /**
   * Builds HTTP parameters for API requests
   */
  buildHttpParams(config: QueryConfig): HttpParams {
    let params = new HttpParams();

    // Add filters
    if (config.filters) {
      const filterString = this.buildFilterString(config.filters);
      if (filterString) {
        params = params.set('filters', filterString);
      }
    }

    // Add sorts
    if (config.sorts && config.sorts.length > 0) {
      const sortString = FilterBuilderUtil.toSortString(config.sorts);
      params = params.set('sorts', sortString);
    }

    // Add pagination
    if (config.pagination) {
      if (config.pagination.skip !== undefined) {
        params = params.set('skip', config.pagination.skip.toString());
      }
      if (config.pagination.limit !== undefined) {
        params = params.set('limit', config.pagination.limit.toString());
      }
      if (config.pagination.cursor) {
        params = params.set('cursor', config.pagination.cursor);
      }
    }

    // Add scopes (as JSON string for complex scope configurations)
    if (config.scopes && config.scopes.length > 0) {
      const scopesString = JSON.stringify(config.scopes);
      params = params.set('scopes', scopesString);
    }

    return params;
  }

  /**
   * Builds a filter string from filters or filter groups
   */
  private buildFilterString(filters: FilterField[] | FilterGroup): string {
    if (Array.isArray(filters)) {
      return FilterBuilderUtil.toFilterString(filters);
    } else {
      return FilterBuilderUtil.filterGroupToString(filters);
    }
  }

  /**
   * Creates a GET request with query parameters
   */
  get<T>(endpoint: string, config?: QueryConfig): Observable<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const params = config ? this.buildHttpParams(config) : new HttpParams();

    return this.http.get<T>(url, { params });
  }

  /**
   * Creates a POST request with query parameters in the body
   */
  post<T>(endpoint: string, body: any, config?: QueryConfig): Observable<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // For POST requests, we can include query config in the body
    const requestBody = {
      ...body,
      ...(config && this.buildRequestBody(config))
    };

    return this.http.post<T>(url, requestBody);
  }

  /**
   * Creates a PATCH request with query parameters in the body
   */
  patch<T>(endpoint: string, body: any, config?: QueryConfig): Observable<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // For PATCH requests, we can include query config in the body
    const requestBody = {
      ...body,
      ...(config && this.buildRequestBody(config))
    };

    return this.http.patch<T>(url, requestBody);
  }

  /**
   * Creates a PUT request with query parameters in the body
   */
  put<T>(endpoint: string, body: any, config?: QueryConfig): Observable<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // For PUT requests, we can include query config in the body
    const requestBody = {
      ...body,
      ...(config && this.buildRequestBody(config))
    };

    return this.http.put<T>(url, requestBody);
  }

  /**
   * Creates a DELETE request with query parameters
   */
  delete<T>(endpoint: string, config?: QueryConfig): Observable<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const params = config ? this.buildHttpParams(config) : new HttpParams();

    return this.http.delete<T>(url, { params });
  }

  /**
   * Builds request body from query config (for POST requests)
   */
  private buildRequestBody(config: QueryConfig): any {
    const body: any = {};

    if (config.filters) {
      body.filters = this.buildFilterString(config.filters);
    }

    if (config.sorts && config.sorts.length > 0) {
      body.sorts = FilterBuilderUtil.toSortString(config.sorts);
    }

    if (config.pagination) {
      if (config.pagination.skip !== undefined) {
        body.skip = config.pagination.skip;
      }
      if (config.pagination.limit !== undefined) {
        body.limit = config.pagination.limit;
      }
      if (config.pagination.cursor) {
        body.cursor = config.pagination.cursor;
      }
    }

    if (config.scopes && config.scopes.length > 0) {
      body.scopes = config.scopes;
    }

    return body;
  }

  /**
   * Creates a search request (commonly used pattern)
   */
  search<T>(
    endpoint: string,
    query: string,
    config?: Omit<QueryConfig, 'filters'>
  ): Observable<T> {
    const searchBody = {
      query,
      ...(config && this.buildRequestBody(config))
    };

    return this.post<T>(endpoint, searchBody);
  }

  /**
   * Creates common filter builders for easy access
   */
  get filters(): CommonFilters {
    return {
      name: FilterBuilderUtil.text('name'),
      description: FilterBuilderUtil.text('description'),
      area_sq_meters: FilterBuilderUtil.numeric('area_sq_meters'),
      id: FilterBuilderUtil.numeric('id'),
      created_at: FilterBuilderUtil.date('created_at'),
      updated_at: FilterBuilderUtil.date('updated_at'),
      is_active: FilterBuilderUtil.boolean('is_active'),
      is_archived: FilterBuilderUtil.boolean('is_archived'),
      geometry: FilterBuilderUtil.spatial('geometry'),
      properties: FilterBuilderUtil.json('properties'),
      metadata: FilterBuilderUtil.json('metadata')
    };
  }

  /**
   * Helper method to create pagination config
   */
  static pagination(
    skip: number = 0,
    limit: number = 20,
    cursor?: string
  ): PaginationConfig {
    return { skip, limit, cursor };
  }

  /**
   * Helper method to create sort config
   */
  static sort(
    field: string,
    direction: SortDirection = SortDirection.ASC
  ): SortConfig {
    return { field, direction };
  }

  /**
   * Helper method to create scope config for project
   */
  static projectScope(
    projectId: string,
    areaFilters?: {
      filters?: string;
      include_ids?: string[];
      exclude_ids?: string[];
    }
  ): ScopeConfig {
    return {
      scope_type: 'project',
      project_scope: { project_id: projectId },
      area_filters: areaFilters
    };
  }

  /**
   * Helper method to create scope config for folder
   */
  static folderScope(
    folderId: string,
    includeSubfolders: boolean = false,
    areaFilters?: {
      filters?: string;
      include_ids?: string[];
      exclude_ids?: string[];
    }
  ): ScopeConfig {
    return {
      scope_type: 'folder',
      folder_scope: {
        folder_id: folderId,
        include_subfolders: includeSubfolders
      },
      area_filters: areaFilters
    };
  }

  /**
   * Creates a complete query configuration
   */
  static createQuery(config: {
    filters?: FilterField[] | FilterGroup;
    sorts?: SortConfig[];
    pagination?: PaginationConfig;
    scopes?: ScopeConfig[];
  }): QueryConfig {
    return config;
  }

  /**
   * Utility method to build URL with query parameters (for debugging or manual requests)
   */
  buildUrl(endpoint: string, config?: QueryConfig): string {
    const url = `${this.baseUrl}${endpoint}`;

    if (!config) {
      return url;
    }

    const params = this.buildHttpParams(config);
    const paramString = params.toString();

    return paramString ? `${url}?${paramString}` : url;
  }

  /**
   * Parses existing query parameters back into QueryConfig
   */
  parseQueryParams(params: HttpParams): Partial<QueryConfig> {
    const config: Partial<QueryConfig> = {};

    // Parse filters
    const filtersParam = params.get('filters');
    if (filtersParam) {
      // Note: This is a simplified parser. For complex filter groups,
      // you might need a more sophisticated parser
      config.filters = this.parseFilterString(filtersParam);
    }

    // Parse sorts
    const sortsParam = params.get('sorts');
    if (sortsParam) {
      config.sorts = this.parseSortString(sortsParam);
    }

    // Parse pagination
    const skipParam = params.get('skip');
    const limitParam = params.get('limit');
    const cursorParam = params.get('cursor');

    if (skipParam || limitParam || cursorParam) {
      config.pagination = {
        skip: skipParam ? parseInt(skipParam, 10) : undefined,
        limit: limitParam ? parseInt(limitParam, 10) : undefined,
        cursor: cursorParam || undefined
      };
    }

    // Parse scopes
    const scopesParam = params.get('scopes');
    if (scopesParam) {
      try {
        config.scopes = JSON.parse(scopesParam);
      } catch (error) {
        console.warn('Failed to parse scopes parameter:', error);
      }
    }

    return config;
  }

  /**
   * Parses filter string back into FilterField array
   * Note: This is a simplified parser for basic filters
   */
  private parseFilterString(filterString: string): FilterField[] {
    const filters: FilterField[] = [];
    const filterParts = filterString.split(',');

    for (const part of filterParts) {
      const [field, operator, value] = part.split(':');
      if (field && operator) {
        filters.push({
          field,
          operator: operator as any,
          value: value || null
        });
      }
    }

    return filters;
  }

  /**
   * Parses sort string back into SortConfig array
   */
  private parseSortString(sortString: string): SortConfig[] {
    const sorts: SortConfig[] = [];
    const sortParts = sortString.split(',');

    for (const part of sortParts) {
      const [field, direction] = part.split(':');
      if (field && direction) {
        sorts.push({
          field,
          direction: direction as SortDirection
        });
      }
    }

    return sorts;
  }
}
